import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";
import {X, ArrowRight, CheckCircle, Clock, Sparkles} from "lucide-react";
import {ShredResponse} from "@/services/MicroServiceService";
import {TourStep} from "@/architecture/project/services/types/tourTypes";

interface GuidedShredsTourProps {
    isActive: boolean;
    currentStep: TourStep;
    shredsArr: ShredResponse[];
    dataModelShred: ShredResponse | undefined;
    featureShreds: ShredResponse[];
    onClose?: () => void;
}

const GuidedShredsTour: React.FC<GuidedShredsTourProps> = ({
                                                               isActive,
                                                               currentStep,
                                                               dataModelShred,
                                                               featureShreds,
                                                               onClose
                                                           }) => {
    const [highlightedElement, setHighlightedElement] = useState<string | null>(null);
    const [tourMessage, setTourMessage] = useState<string>('');
    const [tourTitle, setTourTitle] = useState<string>('');
    const [isVisible, setIsVisible] = useState(false);

    const overlayRef = useRef<HTMLDivElement>(null);
    const messageRef = useRef<HTMLDivElement>(null);
    const animationFrameRef = useRef<number | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const targetElementRef = useRef<Element | null>(null);

    // Create stable dependencies to prevent infinite loops
    const stableDataModelId = useMemo(() => dataModelShred?.shredId, [dataModelShred?.shredId]);
    const stableFeatureShreds = useMemo(() =>
            featureShreds.map(s => ({id: s.shredId, status: s.status, summary: !!s.summary})),
        [featureShreds]
    );

    // Update tour content based on current step - REMOVED CONSOLE LOG TO PREVENT INFINITE LOGS
    useEffect(() => {
        switch (currentStep) {
            case 'GENERATE_SHREDS':
                setHighlightedElement('[data-tour="generate-shreds-card"]');
                setTourTitle('Step 1: Generate Your First Shreds');
                setTourMessage('Click here to generate your initial shreds. Make sure "Include Data Model" is checked to create the foundation of your service.');
                break;

            case 'GENERATE_MODEL':
                setHighlightedElement('[data-tour="generate-model-toggle"]');
                setTourTitle('Step 2: Enable Data Model Generation');
                setTourMessage('Make sure this toggle is ON to include a data model shred in your generation. The data model is essential for your service foundation.');
                break;

            case 'GENERATE_MODEL_SPECS':
                setHighlightedElement(`[data-tour="generate-specs-${stableDataModelId}"]`);
                setTourTitle('Step 3: Generate Model Specifications');
                setTourMessage('Now generate detailed specifications for your data model. This will define the structure and entities of your service.');
                break;

            case 'EXPLORE_MODEL':
                setHighlightedElement(`[data-tour="explore-${stableDataModelId}"]`);
                setTourTitle('Step 4: Explore & Validate Model');
                setTourMessage('Click "Explore" to review and validate your data model. You need to validate it before proceeding to feature development.');
                break;

            case 'WAIT_MODEL_VALIDATION':
                setHighlightedElement(`[data-tour="shred-card-${stableDataModelId}"]`);
                setTourTitle('Step 5: Waiting for Model Validation');
                setTourMessage('Please go to the data model page and validate your model. Once validated, you can proceed to generate feature shreds.');
                break;

            case 'GENERATE_FEATURE_SPECS':
                const featureWithoutSpecs = stableFeatureShreds.find(shred => !shred.summary);
                setHighlightedElement(`[data-tour="generate-specs-${featureWithoutSpecs?.id}"]`);
                setTourTitle('Step 6: Generate Feature Specifications');
                setTourMessage('Now generate specifications for your feature shreds. These will define the business logic and functionality of your service.');
                break;

            case 'EXPLORE_FEATURE':
                const featureWithSpecs = stableFeatureShreds.find(shred =>
                    shred.summary && ['SPECS_GENERATED', 'DONE'].includes(shred.status)
                );
                if (featureWithSpecs) {
                    setHighlightedElement(`[data-tour="explore-${featureWithSpecs.id}"]`);
                } else {
                    setHighlightedElement(null);
                }
                setTourTitle('Step 7: Explore Feature & Generate Code');
                setTourMessage('Excellent! Click "Explore" to review your feature specifications and generate the actual code for your service.');
                break;

            case 'COMPLETED':
                setHighlightedElement(null);
                setTourTitle('🎉 Congratulations!');
                setTourMessage('You\'ve successfully completed the guided tour! You now understand how to create and develop shreds for your microservice.');
                break;

            default:
                setHighlightedElement(null);
                setTourMessage('');
                setTourTitle('');
        }
    }, [currentStep, stableDataModelId, stableFeatureShreds]); // Use stable dependencies

    // Position tour elements with smooth tracking
    const updatePosition = useCallback(() => {
        if (!highlightedElement || !targetElementRef.current) return;

        const element = targetElementRef.current;
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

        // Update overlay spotlight
        if (overlayRef.current) {
            const padding = 10;
            overlayRef.current.style.clipPath = `polygon(
        0% 0%, 
        0% 100%, 
        ${rect.left + scrollLeft - padding}px 100%, 
        ${rect.left + scrollLeft - padding}px ${rect.top + scrollTop - padding}px, 
        ${rect.right + scrollLeft + padding}px ${rect.top + scrollTop - padding}px, 
        ${rect.right + scrollLeft + padding}px ${rect.bottom + scrollTop + padding}px, 
        ${rect.left + scrollLeft - padding}px ${rect.bottom + scrollTop + padding}px, 
        ${rect.left + scrollLeft - padding}px 100%, 
        100% 100%, 
        100% 0%
      )`;
        }

        // Position message box
        if (messageRef.current) {
            const messageBox = messageRef.current;
            const messageRect = messageBox.getBoundingClientRect();

            let top = rect.bottom + scrollTop + 20;
            let left = rect.left + scrollLeft;

            // Adjust horizontal position
            if (left + messageRect.width > window.innerWidth - 20) {
                left = window.innerWidth - messageRect.width - 20;
            }
            if (left < 20) {
                left = 20;
            }

            // Adjust vertical position
            if (top + messageRect.height > window.innerHeight + scrollTop - 20) {
                const topPosition = rect.top + scrollTop - messageRect.height - 20;
                if (topPosition > scrollTop + 20) {
                    top = topPosition;
                } else {
                    top = scrollTop + (window.innerHeight - messageRect.height) / 2;
                }
            }

            messageBox.style.position = 'absolute';
            messageBox.style.top = `${top}px`;
            messageBox.style.left = `${left}px`;
            messageBox.style.zIndex = '10001';
        }
    }, [highlightedElement]);

    // Smooth animation loop for position tracking
    const animatePosition = useCallback(() => {
        updatePosition();
        animationFrameRef.current = requestAnimationFrame(animatePosition);
    }, [updatePosition]);

    // Find and setup target element
    useEffect(() => {
        if (!isActive) {
            setIsVisible(false);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            targetElementRef.current = null;
            return;
        }

        // For COMPLETED step, show without highlighting any element
        if (currentStep === 'COMPLETED') {
            setIsVisible(true);
            return;
        }

        // If no element to highlight, don't show tour
        if (!highlightedElement) {
            setIsVisible(false);
            return;
        }

        // Function to find and setup element
        const setupElement = () => {
            const element = document.querySelector(highlightedElement);
            if (!element) {
                // Retry after a short delay - NO CONSOLE LOG TO PREVENT SPAM
                setTimeout(setupElement, 100);
                return;
            }

            targetElementRef.current = element;

            // Ensure element is visible
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            });

            // Setup intersection observer to track visibility
            if (observerRef.current) {
                observerRef.current.disconnect();
            }

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        // Start animation loop
                        if (!animationFrameRef.current) {
                            animationFrameRef.current = requestAnimationFrame(animatePosition);
                        }
                    }
                },
                {threshold: 0.1}
            );

            observerRef.current.observe(element);
        };

        setupElement();

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [highlightedElement, isActive, animatePosition, currentStep]);

    // Handle scroll and resize events
    useEffect(() => {
        const handleScroll = () => {
            if (isVisible && targetElementRef.current) {
                updatePosition();
            }
        };

        const handleResize = () => {
            if (isVisible && targetElementRef.current) {
                updatePosition();
            }
        };

        window.addEventListener('scroll', handleScroll, {passive: true});
        window.addEventListener('resize', handleResize, {passive: true});

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [isVisible, updatePosition]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    // Don't render if tour is not active or not visible - REMOVED CONSOLE LOG
    if (!isActive || !isVisible) return null;

    const getStepIcon = () => {
        switch (currentStep) {
            case 'COMPLETED':
                return <CheckCircle className="w-6 h-6 text-green-500"/>;
            case 'WAIT_MODEL_VALIDATION':
                return <Clock className="w-6 h-6 text-yellow-500 animate-pulse"/>;
            default:
                return <Sparkles className="w-6 h-6 text-blue-500"/>;
        }
    };

    const getStepNumber = () => {
        const stepNumbers = {
            'GENERATE_SHREDS': 1,
            'GENERATE_MODEL': 2,
            'GENERATE_MODEL_SPECS': 3,
            'EXPLORE_MODEL': 4,
            'WAIT_MODEL_VALIDATION': 5,
            'GENERATE_FEATURE_SPECS': 6,
            'EXPLORE_FEATURE': 7,
            'COMPLETED': '✓'
        };
        return stepNumbers[currentStep];
    };

    // For COMPLETED step, show centered modal without overlay
    if (currentStep === 'COMPLETED') {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
                <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-6 max-w-sm mx-4">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <span className="text-green-600 font-bold text-sm">✓</span>
                            </div>
                            <CheckCircle className="w-6 h-6 text-green-500"/>
                        </div>
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5"/>
                            </button>
                        )}
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-2">{tourTitle}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{tourMessage}</p>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="w-full bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                        >
                            Complete Tour
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Overlay */}
            <div
                ref={overlayRef}
                className="fixed inset-0 bg-black bg-opacity-50 pointer-events-none"
                style={{zIndex: 10000}}
            />

            {/* Message Box */}
            <div
                ref={messageRef}
                className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 p-6 max-w-sm pointer-events-auto"
                style={{zIndex: 10001}}
            >
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-bold text-sm">{getStepNumber()}</span>
                        </div>
                        {getStepIcon()}
                    </div>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-5 h-5"/>
                        </button>
                    )}
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">{tourTitle}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{tourMessage}</p>

                <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-400">
                        Guided Tour • Step {getStepNumber()} of 7
                    </div>
                    {(currentStep as string) === 'COMPLETED' && (
                        <div className="flex items-center text-blue-600 text-sm">
                            <span className="mr-1">Follow the steps</span>
                            <ArrowRight className="w-4 h-4"/>
                        </div>
                    )}
                </div>

                {(currentStep as string) === 'COMPLETED' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="w-full bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                        >
                            Complete Tour
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default GuidedShredsTour;